import React from 'react';
import Points from 'components/Points';
import { TradeSuggestion } from 'types/interfaces/tradeSuggestion';
import { getNetCopy } from 'utils/fundingCopy';

interface Props {
  suggestion: TradeSuggestion;
  /**
   * Inline sits in the partner row when the ledger has taken the axis's place.
   * Stacked is the axis centre column: arrow above, why-line below.
   */
  inline?: boolean;
}

/**
 * The net, in words plus the branded Points pill.
 *
 * Direction and amount come from getNetCopy over the two side totals. That is
 * the same derivation the offer builder, the offer summary card and the
 * zero-state strip already use, and it equals the corrected wire `net` from
 * backend PR #259 (youSendPoints - theySendPoints, positive meaning the viewer
 * receives). Deriving from the totals rather than the wire field means the sign
 * is right both before and after that correction lands: the totals were never
 * in dispute. Swapping the source to the wire `net` afterwards is a no-op.
 *
 * Magenta for paying, green for receiving. Never red for paying points: it
 * reads as an error, and paying points is a normal outcome.
 */
const SuggestionNet: React.FC<Props> = ({ suggestion, inline = false }) => {
  const { netAmount, netPrefix, netDirection, whyLine, netLine } = getNetCopy(
    suggestion.totals.youSendPoints,
    suggestion.totals.theySendPoints
  );

  const words =
    netAmount != null ? (
      <span className="sugg-net-row">
        <span className={`sugg-net-words sugg-net-words--${netDirection}`}>{netPrefix}</span>
        <Points type={inline ? 'small' : 'big'} points={netAmount} />
      </span>
    ) : (
      <span className="sugg-net-words sugg-net-words--even">{netLine}</span>
    );

  if (inline) {
    return (
      <span className="sugg-net sugg-net--inline" aria-label={netLine}>
        {words}
      </span>
    );
  }

  return (
    <div className="sugg-net sugg-net--stacked" aria-label={netLine}>
      <span className="sugg-net-arrow" aria-hidden="true">
        &rarr;
      </span>
      {words}
      {whyLine && <span className="sugg-net-why">{whyLine}</span>}
    </div>
  );
};

export default SuggestionNet;
